import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, Divider, FormControl, Grid,
  IconButton, InputLabel, LinearProgress, Link, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from '@mui/material';
import {
  Info as InfoIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  BugReport as BugReportIcon, AddCircleOutline as AddCircleOutlineIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';
import VitalSignsQueryLink from './VitalSignsQueryLink';
import CreatedVsResolvedChart from './CreatedVsResolvedChart';
import { CaseDetailsDialog } from './TicketJourneyDialog';

const SLA_DAYS = 14;
const COLUMNS = [
  { field: 'id', label: 'ID' },
  { field: 'title', label: 'Title' },
  { field: 'state', label: 'State' },
  { field: 'priority', label: 'Priority' },
  { field: 'team', label: 'Team' },
  { field: 'fixVersion', label: 'Fix Version' },
  { field: 'iterationPath', label: 'Iteration Path' },
  { field: 'customer', label: 'Customer' },
  { field: 'cases', label: 'SF Case' },
  { field: 'assignedTo', label: 'Assigned to' },
  { field: 'age', label: 'Age (days)', align: 'right' }
];

// Same rule the Production Defects overview uses for this list; keep in sync with that page.
const stripAssigneeClutter = (text) => {
  let t = String(text || '').trim().replace(/\s*<[^>]*>\s*/g, ' ').trim();
  let prev;
  do {
    prev = t;
    t = t.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
  } while (t !== prev);
  return t.replace(/\s+/g, ' ').trim();
};

const normalizeAssignee = (assignedTo) => {
  if (assignedTo == null || assignedTo === '') return 'Unassigned';
  const raw = typeof assignedTo === 'object' && assignedTo.displayName
    ? String(assignedTo.displayName).trim()
    : String(assignedTo).trim();
  return stripAssigneeClutter(raw) || 'Unassigned';
};

const adoUrl = (id) => {
  const org = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const project = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const base = org.includes('visualstudio.com') ? `https://${org}` : `https://dev.azure.com/${org}`;
  return `${base}/${encodeURIComponent(project)}/_workitems/edit/${id}`;
};

const priorityValue = (priority) => {
  const match = String(priority || '').match(/P?(\d+)/i);
  return match ? parseInt(match[1], 10) : 999;
};

const ageColor = (age) => {
  if (age == null) return 'default';
  if (age > 180) return '#d32f2f';
  if (age > 90) return '#f57c00';
  if (age > 30) return '#fbc02d';
  return '#388e3c';
};

const InfoTip = ({ title }) => (
  <Tooltip title={title} arrow placement="top">
    <IconButton size="small"><InfoIcon fontSize="small" color="action" /></IconButton>
  </Tooltip>
);

const BUGS_COLOR = '#b71c1c';

const CardMetric = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center', px: 0.5 }}>
    <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
      {label}
    </Typography>
  </Box>
);

const TodayPill = ({ icon, text, bg, color }) => (
  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 2, bgcolor: bg, color }}>
    {icon}
    <Typography variant="caption" sx={{ fontWeight: 700 }}>{text}</Typography>
  </Box>
);

/** Production Bugs summary card; the Non-Closed Bugs by State vs SLA table (same data as Production Defects) carries the SLA split. */
const ProductionBugsCard = ({ open, total, closed, avgPerWeek, newToday, closedToday, rows, totals }) => (
  <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
      background: `linear-gradient(135deg, ${BUGS_COLOR}ee 0%, ${BUGS_COLOR}bb 100%)`
    }}>
      <BugReportIcon sx={{ color: '#fff' }} />
      <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3 }}>
        Production Bugs
      </Typography>
      <Box sx={{ ml: 'auto' }}><VitalSignsQueryLink tile="l3Swat" /></Box>
    </Box>
    <Box sx={{ p: 1.75, flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
        <InfoTip
          title={(
            <Box sx={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
              <div><b>Open</b>: non-closed bugs excluding Resolved. Matches the L3 SWAT tile.</div>
              <div><b>Total</b>: all production bugs in ADO created since 2024-01-01.</div>
              <div><b>Closed</b>: Total minus non-closed bugs. Resolved bugs are not counted here.</div>
              <div><b>Avg/week (3mo)</b>: new production bugs per week over the last 3 months.</div>
              <div><b>New / closed today</b>: bugs created or closed since midnight.</div>
            </Box>
          )}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 1.25 }}>
        <CardMetric label="Open" value={open} color={BUGS_COLOR} />
        <CardMetric label="Total" value={total} color="#555" />
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1.25 }}>
        <CardMetric label="Closed" value={closed} color="#2e7d32" />
        <CardMetric label="Avg/week (3mo)" value={avgPerWeek} color="#555" />
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', gap: 1, py: 1.25 }}>
        <TodayPill
          icon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
          text={`${newToday ?? '—'} new today`}
          bg="#eaf5ea"
          color="#2e7d32"
        />
        <TodayPill
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          text={`${closedToday ?? '—'} closed today`}
          bg="#e3f2fd"
          color="#0277bd"
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
          NON-CLOSED BUGS BY STATE VS SLA
        </Typography>
        <InfoTip title={`Count of non-closed bugs by state, split by whether their age is within or outside the ${SLA_DAYS}-day SLA.`} />
      </Box>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', px: 1.5, py: 0.4, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>State</Typography>
          <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700 }}>In SLA</Typography>
          <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700 }}>Out</Typography>
        </Box>
        {rows.map(([state, counts]) => (
          <Box key={state} sx={{ display: 'flex', px: 1.5, py: 0.4, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ flex: 1 }}>{state}</Typography>
            <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700, color: BUGS_COLOR }}>{counts.within}</Typography>
            <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700, color: BUGS_COLOR }}>{counts.outside}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', px: 1.5, py: 0.4, bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>Total</Typography>
          <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700, color: BUGS_COLOR }}>{totals.within}</Typography>
          <Typography variant="caption" sx={{ width: 56, textAlign: 'right', fontWeight: 700, color: BUGS_COLOR }}>{totals.outside}</Typography>
        </Box>
      </Box>
    </Box>
  </Paper>
);

const SmallStatCard = ({ title, info, footer, children }) => (
  <Paper
    variant="outlined"
    elevation={1}
    sx={{ p: 2, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
        {title}
      </Typography>
      <Box sx={{ position: 'absolute', right: 0 }}><InfoTip title={info} /></Box>
    </Box>
    {children}
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 1 }}>{footer}</Typography>
  </Paper>
);

const CLOSURE_COLORS = { P1: '#c62828', P2: '#e65100', P3: '#1565c0' };

const BugMetricsColumn = ({ metrics }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
    <SmallStatCard
      title="Avg Bugs/Week"
      info="Average number of production bugs created and closed per week over the last 3 months."
      footer={metrics.period || '3 months'}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {metrics.avgIncomingPerWeek?.toFixed(1) || '0.0'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Incoming</Typography>
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
            {metrics.avgClosedPerWeek?.toFixed(1) || '0.0'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Closed</Typography>
        </Box>
      </Box>
    </SmallStatCard>
    <SmallStatCard
      title="Avg Closure Time"
      info="Average time (in days) from creation to closure for P1, P2, and P3 production bugs over the last 3 months (red = P1, orange = P2, blue = P3). Lower values indicate faster resolution."
      footer={metrics.period || '3 months'}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        {['P1', 'P2', 'P3'].map((priority) => {
          const days = metrics.avgClosureTime ? metrics.avgClosureTime[priority] : null;
          return (
            <Typography key={priority} variant="h5" sx={{ fontWeight: 'bold', color: CLOSURE_COLORS[priority] }}>
              {days !== null && days !== undefined ? `${days}d` : 'N/A'}
            </Typography>
          );
        })}
      </Box>
    </SmallStatCard>
  </Box>
);

/** Open (non-closed) production bugs: summary card, averages, created vs resolved and the full list. */
const OpenProductionBugsView = ({ l1Tickets = [] }) => {
  const [caseDetailsId, setCaseDetailsId] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedFixVersion, setSelectedFixVersion] = useState('');
  const [sortField, setSortField] = useState('age');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    get('/api/workitems/production-bugs/public/stats')
      .then((response) => {
        if (cancelled) return;
        if (!response.success) throw new Error(response.message || 'Failed to fetch statistics');
        setStats(response.data);
      })
      .catch((err) => {
        console.error('Error fetching production bug stats:', err);
        if (!cancelled) setError('Failed to load production bugs. Please try again later.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const allBugs = useMemo(() => (stats && stats.allNonClosedBugs) || [], [stats]);
  const filterOptions = (stats && stats.filterOptions) || { states: [], teams: [] };
  const fixVersions = useMemo(
    () => [...new Set(allBugs.map((bug) => bug.fixVersion).filter(Boolean))].sort(),
    [allBugs]
  );

  // Open = non-closed minus Resolved (keep in sync with isOpenBugState in server/utils/productionBugFilters.js).
  const openBugs = useMemo(
    () => allBugs.filter((bug) => String(bug.state || '').trim().toLowerCase() !== 'resolved'),
    [allBugs]
  );

  const sla = useMemo(() => {
    const byState = {};
    allBugs.forEach((bug) => {
      const state = bug.state || 'Unknown';
      if (!byState[state]) byState[state] = { within: 0, outside: 0 };
      if (bug.age != null && bug.age > SLA_DAYS) byState[state].outside += 1;
      else byState[state].within += 1;
    });
    const states = Object.keys(byState).sort();
    const totals = states.reduce(
      (acc, s) => ({ within: acc.within + byState[s].within, outside: acc.outside + byState[s].outside }),
      { within: 0, outside: 0 }
    );
    return { byState, states, totals };
  }, [allBugs]);

  // Same alphabetical order as the Production Defects overview table.
  const cardRows = useMemo(
    () => sla.states.map((state) => [state, sla.byState[state]]),
    [sla]
  );

  const createdVsResolved = useMemo(
    () => (stats && stats.charts && stats.charts.createdVsResolved) || [],
    [stats]
  );

  // Backlog per month: start from today's open count and walk back through the months.
  const backlogData = useMemo(() => {
    const out = [];
    let running = openBugs.length;
    for (let i = createdVsResolved.length - 1; i >= 0; i -= 1) {
      out.unshift({ month: createdVsResolved[i].month, backlog: Math.max(0, running) });
      running = running - (createdVsResolved[i].created || 0) + (createdVsResolved[i].resolved || 0);
    }
    return out;
  }, [createdVsResolved, openBugs]);

  // Open Salesforce cases by the ADO ticket (L3 or related) they are tied to.
  const casesByAdoId = useMemo(() => {
    const byId = new Map();
    (l1Tickets || []).forEach((ticket) => {
      const ids = new Set([
        ...(ticket.l3Ids || []),
        ...(ticket.relatedItems || []).map((item) => item.id)
      ].map(String));
      ids.forEach((id) => byId.set(id, [...(byId.get(id) || []), String(ticket.id)]));
    });
    return byId;
  }, [l1Tickets]);

  const sortedBugs = useMemo(() => {
    const rows = allBugs.map((bug) => ({ ...bug, cases: casesByAdoId.get(String(bug.id)) || [] })).filter((bug) => (
      (!selectedState || bug.state === selectedState)
      && (!selectedTeam || bug.team === selectedTeam)
      && (!selectedFixVersion || bug.fixVersion === selectedFixVersion)
    ));
    const dir = sortDirection === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      if (sortField === 'age' || sortField === 'id') return dir * ((Number(a[sortField]) || 0) - (Number(b[sortField]) || 0));
      if (sortField === 'priority') return dir * (priorityValue(a.priority) - priorityValue(b.priority));
      const va = sortField === 'assignedTo' ? normalizeAssignee(a.assignedTo) : String(a[sortField] || '');
      const vb = sortField === 'assignedTo' ? normalizeAssignee(b.assignedTo) : String(b[sortField] || '');
      return dir * va.toLowerCase().localeCompare(vb.toLowerCase());
    });
  }, [allBugs, casesByAdoId, selectedState, selectedTeam, selectedFixVersion, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDirection('asc'); }
  };

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats) return <Alert severity="info">No production bug data available.</Alert>;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ProductionBugsCard
            open={openBugs.length}
            total={stats.total}
            closed={stats.total != null ? stats.total - allBugs.length : null}
            avgPerWeek={stats.metrics ? stats.metrics.avgIncomingPerWeek : null}
            newToday={stats.metrics ? stats.metrics.newToday : null}
            closedToday={stats.metrics ? stats.metrics.closedToday : null}
            rows={cardRows}
            totals={sla.totals}
          />
        </Grid>
        {stats.metrics && (
          <Grid item xs={12} md={2}>
            <BugMetricsColumn metrics={stats.metrics} />
          </Grid>
        )}
        {createdVsResolved.length > 0 && (
          <Grid item xs={12} md={stats.metrics ? 6 : 8} sx={{ position: 'relative' }}>
            {/* On wide screens the chart is laid over the grid cell, so only the card sets the row height. */}
            <Box sx={{ position: { xs: 'static', md: 'absolute' }, top: 16, left: 16, right: 0, bottom: 0 }}>
              <CreatedVsResolvedChart
                rows={createdVsResolved}
                backlog={backlogData}
                info="Monthly comparison of production bugs created versus resolved over the last 12 months. This chart helps visualize the balance between incoming issues and resolution capacity."
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>All Non-Closed Bugs</Typography>
            {/* Import WIQL: importProductionBugsInternal in bugController.js; list rule: productionBugFilters.js */}
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              ADO query: Project = HighQ Tech AND Work Item Type = Bug AND Created Date &gt; 2024-01-01 AND Issue Source = "Production"
              {' '}| List shows: State does not contain Closed, Done or Removed
            </Typography>
          </Box>
          <InfoTip title="Complete list of all non-closed production bugs. Use filters to narrow down by State, Team, and Fix Version." />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>State</InputLabel>
            <Select value={selectedState} label="State" onChange={(e) => setSelectedState(e.target.value)}>
              <MenuItem value="">All States</MenuItem>
              {filterOptions.states.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Team</InputLabel>
            <Select value={selectedTeam} label="Team" onChange={(e) => setSelectedTeam(e.target.value)}>
              <MenuItem value="">All Teams</MenuItem>
              {filterOptions.teams.map((team) => <MenuItem key={team} value={team}>{team}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Fix Version</InputLabel>
            <Select value={selectedFixVersion} label="Fix Version" onChange={(e) => setSelectedFixVersion(e.target.value)}>
              <MenuItem value="">All Fix Versions</MenuItem>
              {fixVersions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {sortedBugs.length} of {allBugs.length} bugs
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.field}
                    align={col.align}
                    sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(col.field)}
                  >
                    <Box display="flex" alignItems="center" justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                      {col.label}
                      {sortField === col.field && (sortDirection === 'asc'
                        ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        : <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedBugs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {allBugs.length === 0 ? 'No open production bugs.' : 'No bugs found matching the selected filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : sortedBugs.map((bug) => {
                const breached = bug.age != null && bug.age > SLA_DAYS;
                const rowBg = breached ? '#fdecea' : '#edf7ed';
                const rowBgHover = breached ? '#fadbd6' : '#dcefdc';
                return (
                  <TableRow
                    key={bug.id}
                    hover
                    sx={{
                      '& .MuiTableCell-root': { backgroundColor: rowBg, color: '#000' },
                      '&:hover .MuiTableCell-root': { backgroundColor: rowBgHover }
                    }}
                  >
                    <TableCell>
                      <Link href={adoUrl(bug.id)} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        #{bug.id}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{bug.title || 'Untitled'}</Typography>
                    </TableCell>
                    <TableCell><Chip label={bug.state || 'Unknown'} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={bug.priority || 'Unknown'} size="small" variant="outlined" /></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{bug.team || 'Unknown'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{bug.fixVersion || 'Not Specified'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {bug.iterationPath ? bug.iterationPath.split('\\').pop() : 'Not Specified'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{bug.customer || 'Unknown'}</Typography></TableCell>
                    <TableCell>
                      {bug.cases.length === 0
                        ? <Typography variant="body2" color="text.secondary">—</Typography>
                        : bug.cases.map((caseId) => (
                          <Link
                            key={caseId}
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => setCaseDetailsId(caseId)}
                            sx={{ display: 'block', fontWeight: 'medium', color: 'primary.main' }}
                          >
                            {caseId}
                          </Link>
                        ))}
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{normalizeAssignee(bug.assignedTo)}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title={bug.createdDate ? new Date(bug.createdDate).toLocaleString() : 'Created date unknown'}>
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          {bug.age != null
                            ? <Chip label={`${bug.age} days`} size="small" sx={{ backgroundColor: ageColor(bug.age), color: 'white', fontWeight: 'bold' }} />
                            : <Typography variant="body2" color="text.secondary">-</Typography>}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {caseDetailsId && <CaseDetailsDialog caseNumber={caseDetailsId} onClose={() => setCaseDetailsId(null)} />}
    </Box>
  );
};

export default OpenProductionBugsView;
